import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize, forkJoin, map, of, switchMap, timeout } from 'rxjs';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import { EventService, EventDto, EventCreateDto, EventUpdateDto } from '../services/event.service';
import { BookingService, BookingDto, BookingCreateDto, BookingUpdateDto } from '../services/booking.service';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { QuestionAssignmentService } from '../services/question-assignment.service';
import { PortableConfigExportService } from '../services/portable-config-export.service';
import { TeamService } from '../services/team.service';

interface EventRecord {
  id: number;
  bookingId: number;
  name: string;
  date: string;   // YYYY-MM-DD
  time: string;   // HH:MM
  rounds: number;
  status: 'Upcoming' | 'Completed';
  isEnabled: boolean;
  isDeleted: boolean;
  assignedTeamNames: string[];
}

interface BookingRecord {
  id: number;
  organizationId: number;
  name: string;
  date: string;
  time: string;
  status: string;
  isEnabled: boolean;
  isDeleted: boolean;
}

interface BookingOrganizationOption {
  id: number;
  legalName: string;
}

type EventLifecycleAction = 'none' | 'disable' | 'restore' | 'delete' | 'hard-delete';
type BookingLifecycleAction = 'none' | 'disable' | 'restore' | 'delete' | 'hard-delete';

interface ExportRuleStatus {
  label: string;
  details: string;
  passed: boolean;
}

@Component({
  selector: 'app-events-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>
      @if (forbidden) {
        <section class="error-section">
          <h3>Access Denied</h3>
          <p>You do not have access to view events. Please contact your administrator.</p>
        </section>
      }
      @if (!forbidden) {
      <section>
        <!-- ══════════════════ HERO ══════════════════════════════ -->
        <div class="page-hero">
          <div class="page-hero-inner">
            <a routerLink="/" class="home-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
              Home
            </a>
            <div class="hero-top-row">
              <div class="hero-icon hero-icon--blue">
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8"  y1="2" x2="8"  y2="6"/>
                <line x1="3"  y1="10" x2="21" y2="10"/>
              </svg>
              </div>
              <app-hero-user-pill></app-hero-user-pill>
            </div>
            <h1 class="page-title">Bookings & Events</h1>
            <p class="page-sub">Create a booking and manage event(s) for team building events.</p>
          </div>
        </div>

        <!-- ══════════════════ BODY ═══════════════════════════════ -->
        <section class="page-body">
          <div class="page-body-inner">

            <!-- ── Action bar ─────────────────────────────────── -->
            <div class="action-bar">
              <button class="btn btn--primary" (click)="openNewBooking()">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5"  y1="12" x2="19" y2="12"/>
                </svg>
                New Booking
              </button>
              @if (isSiteAdmin) {
                <label class="action-bar-org-picker" for="new-booking-organization-id">
                  <span class="action-bar-org-picker-label">Organization</span>
                  <select
                    id="new-booking-organization-id"
                    class="tool-select action-bar-org-picker-select"
                    [(ngModel)]="selectedBookingOrganizationId">
                    @for (org of bookingOrganizationOptions; track org.id) {
                      <option [ngValue]="org.id">{{ org.legalName }} (ID: {{ org.id }})</option>
                    }
                  </select>
                </label>
              }
            </div>

            <div class="events-card">
              <div class="events-card-hd">
                <span class="events-card-title">Bookings</span>
                <div class="card-tools">
                  <div class="tool-input-wrap">
                    <input class="tool-input" placeholder="Filter bookings…" [(ngModel)]="bookingFilterText" />
                  </div>
                </div>
              </div>
              <div class="events-list">
                @for (booking of filteredBookings; track booking.id) {
                  <div class="event-row" (click)="openEditBooking(booking)">
                    <div class="event-info">
                      <span class="event-name">{{ booking.name }}</span>
                      <span class="event-meta">{{ formatDate(booking.date) }} · {{ formatTime(booking.time) }} · {{ booking.status }}</span>
                    </div>
                  </div>
                } @empty {
                  <div class="events-empty">No bookings yet.</div>
                }
              </div>
            </div>

            <!-- ── Action bar ─────────────────────────────────── -->
            <div class="action-bar">
              <button class="btn btn--primary" (click)="openNew()">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5"  y1="12" x2="19" y2="12"/>
                </svg>
                New Event
              </button>
            </div>

            <!-- Events list -->
            <div class="events-card">
              <div class="events-card-hd">
                <span class="events-card-title">All Events</span>
                <div class="card-tools">
                  <div class="tool-input-wrap">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input class="tool-input" placeholder="Filter events…" [(ngModel)]="filterText" />
                  </div>

                  <select class="tool-select" [(ngModel)]="sortMode">
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="status">Planning status</option>
                  </select>

                  <span class="events-count">
                    {{ filteredEvents.length }} event{{ filteredEvents.length !== 1 ? 's' : '' }}
                  </span>
                </div>
              </div>

              <div class="events-list">
                <div class="events-col-hd">
                  <div class="events-col-hd-spacer"></div>
                  @if (isSiteAdmin) {
                    <span class="events-col-label events-col-label--org">Organization</span>
                  }
                  <span class="events-col-label events-col-label--export">Export</span>
                  <span class="events-col-label">Planning</span>
                  <span class="events-col-label">Booking</span>
                  <div class="events-col-hd-chevron"></div>
                </div>
                @for (evt of filteredEvents; track evt.id) {
                  <div class="event-row" (click)="openEdit(evt)">
                    <div class="status-dot" [ngClass]="'dot--' + evt.status.toLowerCase()"></div>
                    <div class="event-info">
                      <span class="event-name">{{ evt.name }}</span>
                      <span class="event-meta">
                        {{ formatDate(evt.date) }}&nbsp;&nbsp;·&nbsp;&nbsp;{{ formatTime(evt.time) }}&nbsp;&nbsp;·&nbsp;&nbsp;{{ evt.rounds }} round{{ evt.rounds !== 1 ? 's' : '' }}&nbsp;&nbsp;·&nbsp;&nbsp;Teams: {{ getAssignedTeamsLabel(evt) }}
                      </span>
                    </div>
                    @if (isSiteAdmin) {
                      <span class="event-org">{{ getOrganizationName(getBookingById(evt.bookingId)?.organizationId ?? 0) }}</span>
                    }
                    <button
                      type="button"
                      class="btn btn--ghost btn--export"
                      [disabled]="!canExportSettings"
                      (click)="openExportChecklist(evt, $event)">
                      Export Settings
                    </button>
                    <span class="badge" [ngClass]="'badge--' + evt.status.toLowerCase()">
                      {{ evt.status }}
                    </span>
                    <span class="booking-badge">{{ getBookingName(evt.bookingId) }}</span>
                    <div class="row-chevron">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                           viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  </div>
                } @empty {
                  <div class="events-empty">
                    No events yet — create your first event below.
                  </div>
                }
              </div>
            </div>

          </div>
        </section>

        <!-- ══════════════════ MODAL ══════════════════════════════ -->
        @if (isModalOpen) {
          <div class="modal-backdrop" (click)="closeModal()">
            <div class="modal" (click)="$event.stopPropagation()">

              <!-- Header -->
              <div class="modal-hd">
                <h2 class="modal-title">{{ isNewEvent ? 'New Event' : 'Edit Event' }}</h2>
                <div class="modal-hd-actions">
                  <button class="modal-generate" title="Generate" aria-label="Generate">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21.5 2v6h-6"/>
                      <path d="M3 13.5a9 9 0 0 1 15-6.7L21.5 8"/>
                      <path d="M2.5 22v-6h6"/>
                      <path d="M21 10.5a9 9 0 0 1-15 6.7L2.5 16"/>
                    </svg>
                  </button>
                  <button class="modal-close" (click)="closeModal()" aria-label="Close">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="18" y1="6"  x2="6"  y2="18"/>
                      <line x1="6"  y1="6"  x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Fields -->
              <div class="modal-body">
                <div class="field">
                  <label class="field-label" for="evt-name">Event Name</label>
                  <input id="evt-name" class="field-input" type="text"
                         [(ngModel)]="editDraft.name"
                         placeholder="Enter event name">
                </div>

                <div class="field-row">
                  <div class="field">
                    <label class="field-label" for="evt-date">Date</label>
                    <input id="evt-date" class="field-input" type="date" [value]="editDraft.date" readonly>
                  </div>
                  <div class="field">
                    <label class="field-label" for="evt-time">Time</label>
                    <input id="evt-time" class="field-input" type="time" [value]="editDraft.time" readonly>
                  </div>
                </div>

                <div class="field">
                  <label class="field-label" for="evt-booking-id">Booking</label>
                  <select id="evt-booking-id" class="field-select" [(ngModel)]="editDraft.bookingId">
                    @for (booking of bookings(); track booking.id) {
                      <option [ngValue]="booking.id">{{ booking.name }}</option>
                    }
                  </select>
                </div>

                <div class="field">
                  <label class="field-label" for="evt-rounds">Rounds (from assigned questions)</label>
                  <input id="evt-rounds" class="field-input" type="text"
                         [value]="editDraft.rounds" readonly>
                </div>

                <div class="field">
                  <label class="field-label" for="evt-status">Status</label>
                  <select id="evt-status" class="field-select"
                          [(ngModel)]="editDraft.status">
                    <option value="Upcoming">Upcoming</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

              </div>

              <!-- Footer -->
              <div class="modal-ft" [class.modal-ft--end]="isNewEvent">
                @if (!isNewEvent) {
                  <div class="modal-actions">
                    @if (canManageEventLifecycle && !editDraft.isDeleted) {
                      @if (editDraft.isEnabled) {
                        <button
                          class="btn btn--lifecycle-blue"
                          [class.btn--lifecycle-blue-active]="isEventLifecycleActionSelected('disable')"
                          (click)="selectEventLifecycleAction('disable')">
                          Disable
                        </button>
                      } @else {
                        <button
                          class="btn btn--lifecycle-blue"
                          [class.btn--lifecycle-blue-active]="isEventLifecycleActionSelected('restore')"
                          (click)="selectEventLifecycleAction('restore')">
                          Restore
                        </button>
                      }
                      <button
                        class="btn btn--danger"
                        [class.btn--danger-active]="isEventLifecycleActionSelected('delete')"
                        (click)="selectEventLifecycleAction('delete')">
                        Delete
                      </button>
                    }
                    @if (canRestoreDeletedEvents && editDraft.isDeleted) {
                      <button
                        class="btn btn--lifecycle-blue"
                        [class.btn--lifecycle-blue-active]="isEventLifecycleActionSelected('restore')"
                        (click)="selectEventLifecycleAction('restore')">
                        Restore
                      </button>
                    }
                    @if (canHardDeleteEvents) {
                      <button
                        class="btn btn--danger"
                        [class.btn--danger-active]="isEventLifecycleActionSelected('hard-delete')"
                        (click)="selectEventLifecycleAction('hard-delete')">
                        Hard Delete
                      </button>
                    }
                  </div>
                }
                <div class="modal-actions">
                  <button class="btn btn--ghost" (click)="closeModal()">Cancel</button>
                  <button class="btn btn--primary" [disabled]="isEventSaveDisabled()" (click)="saveEvent()">OK</button>
                </div>
              </div>

            </div>
          </div>
        }

        @if (isBookingModalOpen) {
          <div class="modal-backdrop" (click)="closeBookingModal()">
            <div class="modal" (click)="$event.stopPropagation()">
              <div class="modal-hd">
                <h2 class="modal-title">{{ isNewBooking ? 'New Booking' : 'Edit Booking' }}</h2>
                <button class="modal-close" (click)="closeBookingModal()" aria-label="Close">×</button>
              </div>
              <div class="modal-body">
                <div class="field">
                  <label class="field-label">Booking Name</label>
                  <input class="field-input" [(ngModel)]="bookingDraft.name" type="text" />
                </div>
                <div class="field-row">
                  <div class="field">
                    <label class="field-label">Date</label>
                    <input class="field-input" [(ngModel)]="bookingDraft.date" type="date" />
                  </div>
                  <div class="field">
                    <label class="field-label">Time</label>
                    <input class="field-input" [(ngModel)]="bookingDraft.time" type="time" />
                  </div>
                </div>
                <div class="field">
                  <label class="field-label field-label--with-help">
                    Status
                    <span class="field-help" title="Bookings are confirmed on the Scheduling Page.">?</span>
                  </label>
                  <input class="field-input field-input--readonly" [value]="bookingDraft.status" type="text" readonly />
                </div>
              </div>
              <div class="modal-ft" [class.modal-ft--end]="isNewBooking">
                @if (!isNewBooking) {
                  <div class="modal-actions">
                    @if (canManageBookingLifecycle && !bookingDraft.isDeleted) {
                      @if (bookingDraft.isEnabled) {
                        <button
                          class="btn btn--lifecycle-blue"
                          [class.btn--lifecycle-blue-active]="isBookingLifecycleActionSelected('disable')"
                          (click)="selectBookingLifecycleAction('disable')">
                          Disable
                        </button>
                      } @else {
                        <button
                          class="btn btn--lifecycle-blue"
                          [class.btn--lifecycle-blue-active]="isBookingLifecycleActionSelected('restore')"
                          (click)="selectBookingLifecycleAction('restore')">
                          Restore
                        </button>
                      }
                      <button
                        class="btn btn--danger"
                        [class.btn--danger-active]="isBookingLifecycleActionSelected('delete')"
                        (click)="selectBookingLifecycleAction('delete')">
                        Delete
                      </button>
                    }
                    @if (canRestoreDeletedBookings && bookingDraft.isDeleted) {
                      <button
                        class="btn btn--lifecycle-blue"
                        [class.btn--lifecycle-blue-active]="isBookingLifecycleActionSelected('restore')"
                        (click)="selectBookingLifecycleAction('restore')">
                        Restore
                      </button>
                    }
                    @if (canHardDeleteBookings) {
                      <button
                        class="btn btn--danger"
                        [class.btn--danger-active]="isBookingLifecycleActionSelected('hard-delete')"
                        (click)="selectBookingLifecycleAction('hard-delete')">
                        Hard Delete
                      </button>
                    }
                  </div>
                }
                <div class="modal-actions">
                  <button class="btn btn--ghost" (click)="closeBookingModal()">Cancel</button>
                  <button class="btn btn--primary" [disabled]="isBookingSaveDisabled()" (click)="saveBooking()">OK</button>
                </div>
              </div>
            </div>
          </div>
        }

        @if (isExportChecklistOpen) {
          <div class="modal-backdrop" (click)="closeExportChecklist()">
            <div class="modal modal--confirm" (click)="$event.stopPropagation()">
              <div class="modal-hd">
                <h2 class="modal-title">Export Settings</h2>
                <button class="modal-close" (click)="closeExportChecklist()" aria-label="Close">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6"  x2="6"  y2="18"/>
                    <line x1="6"  y1="6"  x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div class="modal-body">
                <p class="confirm-text">{{ exportChecklistTitle }}</p>

                @if (isEvaluatingExportRules) {
                  <p class="confirm-text">Evaluating export rules…</p>
                }

                <div class="export-checklist">
                  @for (rule of exportRules; track rule.label) {
                    <div class="export-rule-row" [class.export-rule-row--failed]="!rule.passed">
                      <span class="export-rule-icon">{{ rule.passed ? '✓' : '✗' }}</span>
                      <div class="export-rule-copy">
                        <strong>{{ rule.label }}</strong>
                        <small>{{ rule.details }}</small>
                      </div>
                    </div>
                  } @empty {
                    <div class="export-rule-row">
                      <span class="export-rule-icon">…</span>
                      <div class="export-rule-copy">
                        <strong>Waiting for rule evaluation</strong>
                        <small>Checklist items will appear automatically when evaluation completes.</small>
                      </div>
                    </div>
                  }
                </div>

                @if (exportChecklistError) {
                  <p class="confirm-text export-error">{{ exportChecklistError }}</p>
                }

                <div class="export-debug" role="status" aria-live="polite">
                  <div><strong>Export status</strong></div>
                  @if (!isEvaluatingExportRules && !canExportCurrentEvent && exportRules.length > 0) {
                    <div class="export-debug-blocked">Export is currently blocked because one or more rules are failing (red ✗).</div>
                  } @else if (!isEvaluatingExportRules && canExportCurrentEvent && exportRules.length > 0) {
                    <div class="export-debug-passed">All export rules are currently passing (green ✓).</div>
                  }
                </div>
              </div>
              <div class="modal-ft modal-ft--end">
                <div class="modal-actions">
                  <button class="btn btn--ghost" (click)="closeExportChecklist()">Cancel</button>
                  <button
                    class="btn btn--primary"
                    [disabled]="isEvaluatingExportRules || isExportingConfiguration"
                    (click)="confirmExportSettings()">
                    {{ isExportingConfiguration ? 'Exporting…' : 'Export' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        }

      </section>
      }
    </div>
  `,
  styles: [`

    /* ── Page shell ─────────────────────────────────────────── */
    .page {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }

    /* ── Hero ───────────────────────────────────────────────── */
    .page-hero {
      background:
        radial-gradient(
          ellipse at 12% 60%,
          color-mix(in srgb, #3b82f6 8%, transparent) 0%,
          transparent 55%
        ),
        linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
      border-bottom: 1px solid var(--color-border);
      padding: 3rem 1.5rem 3.5rem;
    }

    .page-hero-inner {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin: 0 auto;
      max-width: 1280px;
    }

    .home-btn {
      align-items: center;
      align-self: flex-start;
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      color: var(--color-muted);
      display: flex;
      font-size: 0.8rem;
      font-weight: 500;
      gap: 0.35rem;
      padding: 0.32rem 0.7rem;
      text-decoration: none;
      transition: background 0.15s, color 0.15s;
    }

    .home-btn:hover {
      background: color-mix(in srgb, var(--color-surface-2) 90%, transparent);
      color: var(--color-text);
    }

    .hero-top-row {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 0.85rem;
    }

    .hero-icon {
      align-items: center;
      border-radius: 0.75rem;
      display: flex;
      height: 52px;
      justify-content: center;
      width: 52px;
    }

    .hero-icon--blue {
      background: color-mix(in srgb, #3b82f6 16%, transparent);
      color: #60a5fa;
    }

    .page-title {
      color: var(--color-text);
      font-size: clamp(1.6rem, 3vw, 2.2rem);
      font-weight: 800;
      line-height: 1.15;
      margin: 0;
    }

    .page-sub {
      color: var(--color-muted);
      font-size: 0.93rem;
      line-height: 1.6;
      margin: 0;
      max-width: 46ch;
    }

    /* ── Body ───────────────────────────────────────────────── */
    .page-body {
      flex: 1;
      padding: 2.5rem 1.5rem;
    }

    .page-body-inner {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      margin: 0 auto;
      max-width: 1280px;
    }

    /* ── Events card ────────────────────────────────────────── */
    .events-card {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      overflow: hidden;
    }

    .events-card-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      gap: 0.65rem;
      padding: 0.85rem 1.25rem;
      flex-wrap: wrap;
    }

    .events-card-title {
      color: var(--color-text);
      font-size: 0.88rem;
      font-weight: 700;
    }

    .events-count {
      color: var(--color-muted);
      font-size: 0.75rem;
    }

    .card-tools {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      flex-wrap: wrap;
      margin-left: auto;
    }

    .tool-input-wrap {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      border: 1px solid var(--color-border);
      border-radius: 0.55rem;
      background: color-mix(in srgb, var(--color-surface-2) 72%, transparent);
      padding: 0.38rem 0.55rem;
      color: var(--color-muted);
    }

    .tool-input {
      border: none;
      outline: none;
      background: transparent;
      color: var(--color-text);
      min-width: 180px;
      font-size: 0.82rem;
    }

    .tool-input::placeholder {
      color: var(--color-muted);
    }

    .tool-select {
      border: 1px solid var(--color-border);
      border-radius: 0.55rem;
      background: color-mix(in srgb, var(--color-surface-2) 72%, transparent);
      color: var(--color-text);
      font-size: 0.8rem;
      padding: 0.38rem 0.55rem;
    }

    .events-list { padding: 0.2rem 0; }

    .events-col-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      gap: 1rem;
      padding: 0.35rem 1.25rem;
    }

    .events-col-hd-spacer {
      flex: 1;
    }

    .events-col-label {
      color: var(--color-muted);
      flex-shrink: 0;
      font-size: 0.65rem;
      font-weight: 700;
      letter-spacing: 0.07em;
      text-align: center;
      text-transform: uppercase;
      width: 72px;
    }

    .events-col-label--org {
      text-align: left;
      width: 180px;
    }

    .events-col-label--export {
      width: 118px;
    }

    .events-col-hd-chevron {
      flex-shrink: 0;
      width: 14px;
    }

    .event-row {
      align-items: center;
      border-bottom: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent);
      cursor: pointer;
      display: flex;
      gap: 1rem;
      padding: 0.85rem 1.25rem;
      transition: background 0.15s;
    }

    .event-row:last-child { border-bottom: none; }

    .event-row:hover {
      background: color-mix(in srgb, var(--color-accent) 5%, transparent);
    }

    .event-row:hover .row-chevron {
      color: var(--color-accent-soft);
      opacity: 1;
    }

    .status-dot {
      border-radius: 50%;
      flex-shrink: 0;
      height: 8px;
      width: 8px;
    }

    .dot--upcoming   { background: #60a5fa; }
    .dot--completed  { background: var(--color-muted); }

    .event-info {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 0.18rem;
      min-width: 0;
    }

    .event-org {
      color: var(--color-muted);
      flex-shrink: 0;
      font-size: 0.75rem;
      max-width: 180px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 180px;
    }

    .event-name {
      color: var(--color-text);
      font-size: 0.88rem;
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .event-meta {
      color: var(--color-muted);
      font-size: 0.75rem;
    }

    .row-chevron {
      color: var(--color-muted);
      flex-shrink: 0;
      opacity: 0.35;
      transition: color 0.15s, opacity 0.15s;
    }

    .events-empty {
      color: var(--color-muted);
      font-size: 0.83rem;
      padding: 2.5rem;
      text-align: center;
    }

    /* Badges */
    .badge {
      border-radius: 9999px;
      flex-shrink: 0;
      font-size: 0.64rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      padding: 0.18rem 0.55rem;
      text-align: center;
      width: 72px;
    }

    .badge--upcoming {
      background: color-mix(in srgb, var(--color-accent) 17%, transparent);
      color: var(--color-accent-soft);
    }

    .badge--completed {
      background: color-mix(in srgb, var(--color-muted) 14%, transparent);
      color: var(--color-muted);
    }

    /* ── Booking status badges ──────────────────────────────── */
    .booking-badge {
      border-radius: 9999px;
      flex-shrink: 0;
      font-size: 0.64rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      padding: 0.18rem 0.55rem;
      text-align: center;
      white-space: nowrap;
      width: 72px;
    }
    .booking-badge--not-submitted {
      background: color-mix(in srgb, var(--color-muted) 14%, transparent);
      color: var(--color-muted);
    }
    .booking-badge--submitted {
      background: color-mix(in srgb, #0ea5e9 16%, transparent);
      color: #38bdf8;
    }
    .booking-badge--pending {
      background: color-mix(in srgb, #f59e0b 16%, transparent);
      color: #fbbf24;
    }
    .booking-badge--confirmed {
      background: color-mix(in srgb, #22c55e 16%, transparent);
      color: #4ade80;
    }

    /* ── Action bar ─────────────────────────────────────────── */
    .action-bar {
      align-items: center;
      display: flex;
      gap: 0.75rem;
      padding-top: 0.25rem;
    }

    .action-bar-org-picker {
      align-items: center;
      display: inline-flex;
      gap: 0.45rem;
    }

    .action-bar-org-picker-label {
      color: var(--color-muted);
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    .action-bar-org-picker-select {
      min-width: 260px;
    }

    /* ── Buttons ────────────────────────────────────────────── */
    .btn {
      align-items: center;
      border: 1px solid transparent;
      border-radius: 0.5rem;
      cursor: pointer;
      display: inline-flex;
      font-size: 0.83rem;
      font-weight: 600;
      gap: 0.4rem;
      padding: 0.5rem 1rem;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }

    .btn--primary { background: #3b82f6; color: #fff; }
    .btn--primary:hover { background: color-mix(in srgb, #3b82f6 82%, #fff); }

    .btn--ghost {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border-color: var(--color-border);
      color: var(--color-muted);
    }
    .btn--ghost:hover { color: var(--color-text); }

    .btn--export {
      padding: 0.35rem 0.65rem;
      white-space: nowrap;
    }

    .btn--danger {
      background: color-mix(in srgb, #ef4444 16%, transparent);
      border-color: color-mix(in srgb, #ef4444 28%, transparent);
      color: #f87171;
    }
    .btn--danger:hover {
      background: color-mix(in srgb, #ef4444 26%, transparent);
      border-color: color-mix(in srgb, #ef4444 45%, transparent);
    }

    .btn--lifecycle-blue {
      background: color-mix(in srgb, #38bdf8 20%, transparent);
      border-color: color-mix(in srgb, #38bdf8 35%, transparent);
      color: #7dd3fc;
    }

    .btn--lifecycle-blue:hover {
      background: color-mix(in srgb, #38bdf8 30%, transparent);
      border-color: color-mix(in srgb, #38bdf8 52%, transparent);
      color: #bae6fd;
    }

    .btn--lifecycle-blue-active {
      background: color-mix(in srgb, #38bdf8 42%, transparent);
      border-color: color-mix(in srgb, #38bdf8 70%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, #38bdf8 28%, transparent), 0 0 18px color-mix(in srgb, #38bdf8 40%, transparent);
      color: #e0f2fe;
    }

    .btn--danger-active {
      background: color-mix(in srgb, #ef4444 34%, transparent);
      border-color: color-mix(in srgb, #ef4444 62%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, #ef4444 26%, transparent), 0 0 18px color-mix(in srgb, #ef4444 38%, transparent);
      color: #fecaca;
    }

    /* ── Modal ──────────────────────────────────────────────── */
    .modal-backdrop {
      align-items: center;
      background: rgba(0, 0, 0, 0.65);
      backdrop-filter: blur(4px);
      display: flex;
      inset: 0;
      justify-content: center;
      padding: 1.5rem;
      position: fixed;
      z-index: 1000;
    }

    .modal {
      background: var(--color-bg-end);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
      max-width: 480px;
      width: 100%;
    }

    .modal--confirm {
      max-width: 420px;
    }

    .modal-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 1.1rem 1.4rem;
    }

    .modal-hd-actions {
      align-items: center;
      display: flex;
      gap: 0.35rem;
    }

    .modal-title { color: var(--color-text); font-size: 1rem; font-weight: 700; margin: 0; }

    .modal-generate,
    .modal-close {
      align-items: center;
      background: none;
      border: none;
      border-radius: 0.4rem;
      color: var(--color-muted);
      cursor: pointer;
      display: flex;
      padding: 0.25rem;
      transition: color 0.15s;
    }
    .modal-generate:hover,
    .modal-close:hover { color: var(--color-text); }

    .modal-body { display: flex; flex-direction: column; gap: 1.1rem; padding: 1.4rem; }

    .confirm-text {
      color: var(--color-text);
      font-size: 0.88rem;
      line-height: 1.6;
      margin: 0;
    }

    .export-checklist {
      display: grid;
      gap: 0.65rem;
    }

    .export-rule-row {
      align-items: flex-start;
      background: color-mix(in srgb, var(--color-surface-2) 70%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.55rem;
      display: grid;
      gap: 0.5rem;
      grid-template-columns: 18px 1fr;
      padding: 0.55rem 0.7rem;
    }

    .export-rule-row--failed {
      border-color: color-mix(in srgb, #ef4444 45%, var(--color-border));
    }

    .export-rule-icon {
      color: #4ade80;
      font-weight: 700;
      line-height: 1.4;
    }

    .export-rule-row--failed .export-rule-icon {
      color: #f87171;
    }

    .export-rule-copy {
      display: grid;
      gap: 0.2rem;
    }

    .export-rule-copy strong {
      color: var(--color-text);
      font-size: 0.8rem;
    }

    .export-rule-copy small {
      color: var(--color-muted);
      font-size: 0.75rem;
      line-height: 1.4;
    }

    .export-error {
      color: #fca5a5;
    }

    .export-debug {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px dashed var(--color-border);
      border-radius: 0.5rem;
      color: var(--color-muted);
      display: grid;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      font-size: 0.72rem;
      gap: 0.2rem;
      padding: 0.55rem 0.7rem;
    }

    .export-debug-blocked {
      color: #fca5a5;
      margin-top: 0.25rem;
    }

    .export-debug-passed {
      color: #86efac;
      margin-top: 0.25rem;
    }

    .modal-ft {
      align-items: center;
      border-top: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 1rem 1.4rem;
    }
    .modal-ft--end { justify-content: flex-end; }
    .modal-actions { display: flex; gap: 0.5rem; }

    /* ── Form fields ────────────────────────────────────────── */
    .field { display: flex; flex-direction: column; gap: 0.4rem; }
    .field-row { display: grid; gap: 0.75rem; grid-template-columns: 1fr 1fr; }

    .field-label {
      color: var(--color-muted);
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.07em;
      text-transform: uppercase;
    }

    .field-label--with-help {
      align-items: center;
      display: inline-flex;
      gap: 0.4rem;
    }

    .field-help {
      align-items: center;
      border: 1px solid var(--color-border);
      border-radius: 9999px;
      color: var(--color-muted);
      cursor: help;
      display: inline-flex;
      font-size: 0.65rem;
      font-weight: 700;
      height: 14px;
      justify-content: center;
      line-height: 1;
      width: 14px;
    }

    .field-input,
    .field-select {
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      box-sizing: border-box;
      color: var(--color-text);
      color-scheme: dark;
      font-size: 0.88rem;
      padding: 0.52rem 0.75rem;
      transition: border-color 0.15s;
      width: 100%;
    }

    .field-input:focus,
    .field-select:focus {
      border-color: var(--color-accent);
      outline: none;
    }

    .field-input::placeholder { color: var(--color-muted); }

    .field-input--readonly {
      color: color-mix(in srgb, var(--color-text) 65%, transparent);
      cursor: not-allowed;
    }

    .field-select option {
      background: var(--color-bg-end);
      color: var(--color-text);
    }

    /* ── Responsive ─────────────────────────────────────────── */
    @media (max-width: 520px) {
      .field-row { grid-template-columns: 1fr; }
    }

    .error-section { color: #b91c1c; background: #fef2f2; border: 1px solid #fca5a5; padding: 1rem; border-radius: 6px; margin: 2rem 0; }
  `]
})
export class EventsPageComponent implements OnInit {
  private readonly requestTimeoutMs = 12000;
  private readonly exportPackageTimeoutMs = 300000;

  private readonly authService = inject(AuthService);
  private readonly eventService = inject(EventService);
  private readonly bookingService = inject(BookingService);
  private readonly organizationService = inject(OrganizationService);
  private readonly teamService = inject(TeamService);
  private readonly questionAssignmentService = inject(QuestionAssignmentService);
  private readonly portableConfigExportService = inject(PortableConfigExportService);
  private readonly cdr = inject(ChangeDetectorRef);

  // Fallback only. Real value is resolved from loaded event data.
  private readonly fallbackOrganizationId = 1;
  private currentOrganizationId: number | null = null;
  private readonly organizationNamesById = new Map<number, string>();
  private readonly organizationLookupInFlight = new Set<number>();
  isSiteAdmin = false;
  bookingOrganizationOptions: BookingOrganizationOption[] = [];
  selectedBookingOrganizationId: number | null = null;

  readonly events = signal<EventRecord[]>([]);
  readonly bookings = signal<BookingRecord[]>([]);

  isModalOpen = false;
  isNewEvent  = false;
  editDraft: EventRecord = { id: 0, bookingId: 0, name: '', date: '', time: '', rounds: 0, status: 'Upcoming', isEnabled: true, isDeleted: false, assignedTeamNames: [] };
  pendingEventLifecycleAction: EventLifecycleAction = 'none';
  canManageEventLifecycle = false;
  canRestoreDeletedEvents = false;
  canHardDeleteEvents = false;
  isBookingModalOpen = false;
  isNewBooking = false;
  bookingDraft: BookingRecord = { id: 0, organizationId: 0, name: '', date: '', time: '', status: 'Scheduled', isEnabled: true, isDeleted: false };
  pendingBookingLifecycleAction: BookingLifecycleAction = 'none';
  canManageBookingLifecycle = false;
  canRestoreDeletedBookings = false;
  canHardDeleteBookings = false;
  filterText = '';
  bookingFilterText = '';
  sortMode: 'newest' | 'oldest' | 'name-asc' | 'name-desc' | 'status' = 'newest';
  canExportSettings = false;

  isExportChecklistOpen = false;
  isEvaluatingExportRules = false;
  isExportingConfiguration = false;
  canExportCurrentEvent = false;
  exportChecklistError: string | null = null;
  exportChecklistTitle = 'Validating rules for selected event.';
  exportRules: ExportRuleStatus[] = [];
  exportEvent: EventRecord | null = null;
  private exportEvaluationWatchdogId: number | null = null;

  // Fix: Add forbidden property for template binding
  forbidden = false;

  ngOnInit(): void {
    void this.initializePageContext();
  }

  private async initializePageContext(): Promise<void> {
    await this.initializeRoleContext();
    this.loadOrganizationLabels();
    this.loadOrganizationContext();
    this.loadEvents();
  }

  private async initializeRoleContext(): Promise<void> {
    const [isSiteAdmin, isOrgAdmin, isEventPlanner] = await Promise.all([
      this.authService.hasRoleAsync('SiteAdmin'),
      this.authService.hasRoleAsync('OrgAdmin'),
      this.authService.hasRoleAsync('EventPlanner')
    ]);

    this.isSiteAdmin = isSiteAdmin;
    this.canExportSettings = isSiteAdmin || isOrgAdmin || isEventPlanner;
    this.canManageEventLifecycle = isSiteAdmin || isOrgAdmin || isEventPlanner;
    this.canRestoreDeletedEvents = isSiteAdmin;
    this.canHardDeleteEvents = isSiteAdmin;
    this.canManageBookingLifecycle = isSiteAdmin || isOrgAdmin || isEventPlanner;
    this.canRestoreDeletedBookings = isSiteAdmin;
    this.canHardDeleteBookings = isSiteAdmin;
  }

  private loadOrganizationLabels(): void {
    const request$ = this.isSiteAdmin
      ? this.organizationService.getAll()
      : this.organizationService.getMyOrganization().pipe(map(org => [org]));

    request$.subscribe({
      next: (organizations) => {
        this.applyOrganizationLabels(organizations);
      },
      error: () => {
        this.organizationService.getMyOrganization().subscribe({
          next: (org) => this.applyOrganizationLabels([org]),
          error: () => {
            // Keep fallback labels when org lookup is unavailable.
          }
        });
      }
    });
  }

  private applyOrganizationLabels(organizations: OrganizationDto[]): void {
    this.organizationNamesById.clear();
    for (const org of organizations) {
      const displayName = this.getOrganizationDisplayName(org);
      if (displayName) {
        this.organizationNamesById.set(org.id, displayName);
      }
    }

    if (this.isSiteAdmin) {
      this.bookingOrganizationOptions = [...organizations]
        .sort((a, b) => a.legalName.localeCompare(b.legalName))
        .map(org => ({
          id: org.id,
          legalName: org.legalName
        }));

      const selectionExists = this.selectedBookingOrganizationId !== null
        && this.bookingOrganizationOptions.some(org => org.id === this.selectedBookingOrganizationId);

      if (!selectionExists) {
        this.selectedBookingOrganizationId = this.currentOrganizationId;
      }
    }
  }

  private loadOrganizationContext(): void {
    this.organizationService.getMyOrganization().subscribe({
      next: (org) => {
        this.currentOrganizationId = org.id;
        if (this.isSiteAdmin && this.selectedBookingOrganizationId === null) {
          this.selectedBookingOrganizationId = org.id;
        }
      },
      error: (err) => {
        console.error('Failed to load current organization:', err);
      }
    });
  }

  private loadEvents(): void {
    forkJoin({
      bookings: this.bookingService.getAll(),
      events: this.eventService.getAll()
    }).subscribe({
      next: ({ bookings, events }) => {
        const bookingRecords = bookings.map(dto => this.bookingDtoToRecord(dto));
        this.bookings.set(bookingRecords);
        const records = events.map(dto => this.dtoToRecord(dto, bookingRecords));
        this.events.set(records);
        this.ensureOrganizationLabelsFromEvents(records);
        if (bookingRecords.length > 0) {
          this.currentOrganizationId = bookingRecords[0].organizationId;
        }
      },
      error: (err) => {
        console.error('Failed to load events:', err);
        this.events.set([]);
        this.bookings.set([]);
      }
    });
  }

  private ensureOrganizationLabelsFromEvents(records: EventRecord[]): void {
    const missingOrgIds = [...new Set(records.map(r => this.getBookingById(r.bookingId)?.organizationId ?? 0))]
      .filter(id => id > 0 && !this.organizationNamesById.has(id));

    for (const orgId of missingOrgIds) {
      this.fetchOrganizationDisplayName(orgId);
    }
  }

  private getOrganizationDisplayName(org: OrganizationDto | null | undefined): string | null {
    const displayName = org?.displayName?.trim();
    return displayName ? displayName : null;
  }

  private fetchOrganizationDisplayName(orgId: number): void {
    if (orgId <= 0 || this.organizationNamesById.has(orgId) || this.organizationLookupInFlight.has(orgId)) {
      return;
    }

    if (!this.isSiteAdmin) {
      return;
    }

    this.organizationLookupInFlight.add(orgId);
    this.organizationService.getById(orgId).subscribe({
      next: (org) => {
        const displayName = this.getOrganizationDisplayName(org);
        if (displayName) {
          this.organizationNamesById.set(org.id, displayName);
          this.events.update(list => [...list]);
        }
      },
      error: () => {
        // Keep loading fallback when org lookup is unavailable.
      },
      complete: () => {
        this.organizationLookupInFlight.delete(orgId);
      }
    });
  }

  private bookingDtoToRecord(dto: BookingDto): BookingRecord {
    const dt = new Date(dto.scheduledDateTime);
    const date = dt.toISOString().split('T')[0];
    const time = dt.toTimeString().slice(0, 5);
    const lifecycleState = this.getBookingLifecycleState(dto.status ?? 'Scheduled');

    return {
      id: dto.id,
      organizationId: dto.organizationId,
      name: dto.name,
      date,
      time,
      status: dto.status ?? 'Scheduled',
      isEnabled: lifecycleState.isEnabled,
      isDeleted: lifecycleState.isDeleted
    };
  }

  private dtoToRecord(dto: EventDto, bookings?: BookingRecord[]): EventRecord {
    const booking = (bookings ?? this.bookings()).find(b => b.id === dto.bookingId);
    return {
      id: dto.id,
      bookingId: dto.bookingId,
      name: dto.name,
      date: booking?.date ?? this.todayDate(),
      time: booking?.time ?? this.nowTime(),
      rounds: dto.numberOfRounds,
      status: dto.status as 'Upcoming' | 'Completed',
      isEnabled: dto.isEnabled ?? true,
      isDeleted: dto.isDeleted ?? false,
      assignedTeamNames: this.extractAssignedTeamNames(dto)
    };
  }

  private extractAssignedTeamNames(dto: EventDto): string[] {
    const assignments = dto.eventTeams ?? dto.teams ?? [];
    const teamNames = assignments
      .map(assignment => assignment.team?.name?.trim())
      .filter((name): name is string => Boolean(name));

    return [...new Set(teamNames)];
  }

  private composeScheduledDateTime(record: { date: string; time: string }): string {
    const date = record.date?.trim();
    const time = record.time?.trim();

    if (!date || !time) {
      throw new Error('Date and time are required.');
    }

    const dt = new Date(`${date}T${time}`);
    if (Number.isNaN(dt.getTime())) {
      throw new Error('Invalid date/time value.');
    }

    return dt.toISOString();
  }

  private recordToCreateDto(record: EventRecord): EventCreateDto {
    if (!record.bookingId || record.bookingId <= 0) {
      throw new Error('Booking is required for event creation.');
    }

    return {
      bookingId: record.bookingId,
      name: record.name,
    };
  }

  private recordToUpdateDto(record: EventRecord): EventUpdateDto {
    return {
      bookingId: record.bookingId,
      name: record.name,
      status: record.status,
    };
  }

  private bookingRecordToCreateDto(record: BookingRecord): BookingCreateDto {
    const resolvedOrganizationId = this.resolveBookingOrganizationId(record);

    if (!resolvedOrganizationId || resolvedOrganizationId <= 0) {
      throw new Error('Organization context is missing for booking creation.');
    }

    return {
      name: record.name,
      organizationId: resolvedOrganizationId,
      scheduledDateTime: this.composeScheduledDateTime(record),
      status: record.status
    };
  }

  private resolveBookingOrganizationId(record: BookingRecord): number {
    if (this.isSiteAdmin) {
      const selectedOrganizationId = this.selectedBookingOrganizationId ?? record.organizationId;
      if (selectedOrganizationId && selectedOrganizationId > 0) {
        return selectedOrganizationId;
      }
    }

    return this.currentOrganizationId ?? this.fallbackOrganizationId;
  }

  private bookingRecordToUpdateDto(record: BookingRecord): BookingUpdateDto {
    return {
      name: record.name,
      scheduledDateTime: this.composeScheduledDateTime(record),
      status: record.status
    };
  }

  getBookingById(bookingId: number): BookingRecord | undefined {
    return this.bookings().find(b => b.id === bookingId);
  }

  openEdit(evt: EventRecord): void {
    this.editDraft   = { ...evt };
    this.isNewEvent  = false;
    this.pendingEventLifecycleAction = 'none';
    this.isModalOpen = true;
  }

  openNew(): void {
    const defaultBookingId = this.bookings().length > 0 ? this.bookings()[0].id : 0;
    const defaultBooking = this.getBookingById(defaultBookingId);

    this.editDraft   = {
      id: 0,
      bookingId: defaultBookingId,
      name: '',
      date: defaultBooking?.date ?? this.todayDate(),
      time: defaultBooking?.time ?? this.nowTime(),
      rounds: 0,
      status: 'Upcoming',
      isEnabled: true,
      isDeleted: false,
      assignedTeamNames: []
    };
    this.isNewEvent  = true;
    this.pendingEventLifecycleAction = 'none';
    this.isModalOpen = true;
  }

  saveEvent(): void {
    if (!this.editDraft.name.trim()) return;
    if (!this.editDraft.date || !this.editDraft.time) return;

    try {
      if (this.isNewEvent) {
        const createDto = this.recordToCreateDto(this.editDraft);
        this.eventService.create(createDto).subscribe({
          next: (dto) => {
            const newRecord = this.dtoToRecord(dto);
            this.events.update(list => [...list, newRecord]);
            this.closeModal();
          },
          error: (err) => console.error('Failed to create event:', err)
        });
      } else {
        if (this.pendingEventLifecycleAction === 'hard-delete') {
          this.executePendingEventLifecycleAction(() => this.closeModal());
          return;
        }

        const updateDto = this.recordToUpdateDto(this.editDraft);
        this.eventService.update(this.editDraft.id, updateDto).subscribe({
          next: (dto) => {
            const updatedRecord = this.dtoToRecord(dto);
            this.events.update(list =>
              list.map(e => e.id === updatedRecord.id ? updatedRecord : e)
            );
            this.editDraft = { ...updatedRecord };
            this.executePendingEventLifecycleAction(() => this.closeModal());
          },
          error: (err) => console.error('Failed to update event:', err)
        });
      }
    } catch (err) {
      console.error('Invalid event draft:', err);
    }
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.pendingEventLifecycleAction = 'none';
  }

  openEditBooking(booking: BookingRecord): void {
    this.bookingDraft = { ...booking };
    this.isNewBooking = false;
    this.pendingBookingLifecycleAction = 'none';
    this.isBookingModalOpen = true;
  }

  openNewBooking(): void {
    const selectedOrganizationId = this.resolveBookingOrganizationId(this.bookingDraft);

    this.bookingDraft = {
      id: 0,
      organizationId: selectedOrganizationId,
      name: '',
      date: this.todayDate(),
      time: this.nowTime(),
      status: 'Pending',
      isEnabled: true,
      isDeleted: false
    };
    this.isNewBooking = true;
    this.pendingBookingLifecycleAction = 'none';
    this.isBookingModalOpen = true;
  }

  saveBooking(): void {
    if (!this.bookingDraft.name.trim()) return;
    if (!this.bookingDraft.date || !this.bookingDraft.time) return;

    try {
      if (this.isNewBooking) {
        const createDto = this.bookingRecordToCreateDto(this.bookingDraft);
        this.bookingService.create(createDto).subscribe({
          next: (dto) => {
            const record = this.bookingDtoToRecord(dto);
            this.bookings.update(list => [record, ...list]);
            this.closeBookingModal();
            this.loadEvents();
          },
          error: (err) => console.error('Failed to create booking:', err)
        });
      } else {
        if (this.pendingBookingLifecycleAction === 'hard-delete') {
          this.executePendingBookingLifecycleAction(() => this.closeBookingModal());
          return;
        }

        const updateDto = this.bookingRecordToUpdateDto(this.bookingDraft);
        this.bookingService.update(this.bookingDraft.id, updateDto).subscribe({
          next: (dto) => {
            const record = this.bookingDtoToRecord(dto);
            this.bookings.update(list => list.map(b => b.id === record.id ? record : b));
            this.bookingDraft = { ...record };
            this.executePendingBookingLifecycleAction(() => {
              this.closeBookingModal();
              this.loadEvents();
            });
          },
          error: (err) => console.error('Failed to update booking:', err)
        });
      }
    } catch (err) {
      console.error('Invalid booking draft:', err);
    }
  }

  closeBookingModal(): void {
    this.isBookingModalOpen = false;
    this.pendingBookingLifecycleAction = 'none';
  }

  selectEventLifecycleAction(action: EventLifecycleAction): void {
    if (action === 'none') {
      this.pendingEventLifecycleAction = 'none';
      return;
    }

    this.pendingEventLifecycleAction = this.pendingEventLifecycleAction === action ? 'none' : action;
  }

  isEventLifecycleActionSelected(action: EventLifecycleAction): boolean {
    return this.pendingEventLifecycleAction === action;
  }

  selectBookingLifecycleAction(action: BookingLifecycleAction): void {
    if (action === 'none') {
      this.pendingBookingLifecycleAction = 'none';
      return;
    }

    this.pendingBookingLifecycleAction = this.pendingBookingLifecycleAction === action ? 'none' : action;
  }

  isBookingLifecycleActionSelected(action: BookingLifecycleAction): boolean {
    return this.pendingBookingLifecycleAction === action;
  }

  isEventSaveDisabled(): boolean {
    if (this.isNewEvent) {
      return false;
    }

    return this.editDraft.isDeleted && this.pendingEventLifecycleAction === 'none';
  }

  isBookingSaveDisabled(): boolean {
    if (this.isNewBooking) {
      return false;
    }

    return this.bookingDraft.isDeleted && this.pendingBookingLifecycleAction === 'none';
  }

  private executePendingEventLifecycleAction(onComplete: () => void): void {
    if (this.pendingEventLifecycleAction === 'none') {
      onComplete();
      return;
    }

    if (this.pendingEventLifecycleAction === 'hard-delete') {
      const confirmed = window.confirm('Permanently delete this event? This cannot be undone.');
      if (!confirmed) {
        return;
      }

      this.eventService.hardDelete(this.editDraft.id).subscribe({
        next: () => {
          this.events.update(list => list.filter(e => e.id !== this.editDraft.id));
          onComplete();
        },
        error: (err) => console.error('Failed to hard delete event:', err)
      });
      return;
    }

    if (this.pendingEventLifecycleAction === 'delete') {
      const confirmed = window.confirm('Delete this event on OK confirmation? This performs a soft delete and disables the event.');
      if (!confirmed) {
        return;
      }

      this.eventService.delete(this.editDraft.id).subscribe({
        next: () => {
          this.editDraft = { ...this.editDraft, isDeleted: true, isEnabled: false };
          this.events.update(list => list.map(e => e.id === this.editDraft.id ? { ...e, isDeleted: true, isEnabled: false } : e));
          onComplete();
        },
        error: (err) => console.error('Failed to delete event:', err)
      });
      return;
    }

    if (this.pendingEventLifecycleAction === 'disable') {
      this.eventService.setEnabled(this.editDraft.id, false).subscribe({
        next: (dto) => {
          const updated = this.dtoToRecord(dto);
          this.editDraft = { ...updated };
          this.events.update(list => list.map(e => e.id === updated.id ? updated : e));
          onComplete();
        },
        error: (err) => console.error('Failed to disable event:', err)
      });
      return;
    }

    const restoreRequest = this.editDraft.isDeleted
      ? this.eventService.restore(this.editDraft.id)
      : this.eventService.setEnabled(this.editDraft.id, true);

    restoreRequest.subscribe({
      next: (dto) => {
        const updated = this.dtoToRecord(dto);
        this.editDraft = { ...updated };
        this.events.update(list => list.map(e => e.id === updated.id ? updated : e));
        onComplete();
      },
      error: (err) => console.error('Failed to restore event:', err)
    });
  }

  private executePendingBookingLifecycleAction(onComplete: () => void): void {
    if (this.pendingBookingLifecycleAction === 'none') {
      onComplete();
      return;
    }

    if (this.pendingBookingLifecycleAction === 'hard-delete') {
      const confirmed = window.confirm('Permanently delete this booking? This cannot be undone.');
      if (!confirmed) {
        return;
      }

      this.bookingService.delete(this.bookingDraft.id).subscribe({
        next: () => {
          this.bookings.update(list => list.filter(b => b.id !== this.bookingDraft.id));
          onComplete();
        },
        error: (err) => console.error('Failed to hard delete booking:', err)
      });
      return;
    }

    if (this.pendingBookingLifecycleAction === 'delete') {
      const confirmed = window.confirm('Delete this booking on OK confirmation?');
      if (!confirmed) {
        return;
      }

      const deleteDto = this.bookingRecordToUpdateDto({
        ...this.bookingDraft,
        status: 'Deleted'
      });

      this.bookingService.update(this.bookingDraft.id, deleteDto).subscribe({
        next: (dto) => {
          const updated = this.bookingDtoToRecord(dto);
          this.bookingDraft = { ...updated };
          this.bookings.update(list => list.map(b => b.id === updated.id ? updated : b));
          onComplete();
        },
        error: (err) => console.error('Failed to delete booking:', err)
      });
      return;
    }

    const targetStatus = this.pendingBookingLifecycleAction === 'disable'
      ? 'Disabled'
      : 'Pending';

    const lifecycleDto = this.bookingRecordToUpdateDto({
      ...this.bookingDraft,
      status: targetStatus
    });

    this.bookingService.update(this.bookingDraft.id, lifecycleDto).subscribe({
      next: (dto) => {
        const updated = this.bookingDtoToRecord(dto);
        this.bookingDraft = { ...updated };
        this.bookings.update(list => list.map(b => b.id === updated.id ? updated : b));
        onComplete();
      },
      error: (err) => console.error('Failed to update booking lifecycle state:', err)
    });
  }

  private getBookingLifecycleState(status: string): { isEnabled: boolean; isDeleted: boolean } {
    const normalized = status.trim().toLowerCase();

    if (normalized === 'deleted') {
      return { isEnabled: false, isDeleted: true };
    }

    if (normalized === 'disabled') {
      return { isEnabled: false, isDeleted: false };
    }

    return { isEnabled: true, isDeleted: false };
  }

  getBookingName(bookingId: number): string {
    return this.getBookingById(bookingId)?.name ?? 'Unknown booking';
  }

  get filteredBookings(): BookingRecord[] {
    const query = this.bookingFilterText.trim().toLowerCase();
    const all = this.bookings();

    if (!query) {
      return [...all].sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time));
    }

    return all
      .filter(b =>
        b.name.toLowerCase().includes(query) ||
        b.status.toLowerCase().includes(query) ||
        this.getOrganizationName(b.organizationId).toLowerCase().includes(query)
      )
      .sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time));
  }

  openExportChecklist(evt: EventRecord, domEvent: MouseEvent): void {
    domEvent.stopPropagation();

    if (!this.canExportSettings) {
      return;
    }

    this.exportEvent = evt;
    this.exportChecklistTitle = `Event: ${evt.name}`;
    this.exportRules = [];
    this.exportChecklistError = null;
    this.canExportCurrentEvent = false;
    this.isExportChecklistOpen = true;
    this.isEvaluatingExportRules = true;

    if (this.exportEvaluationWatchdogId !== null) {
      window.clearTimeout(this.exportEvaluationWatchdogId);
    }
    this.exportEvaluationWatchdogId = window.setTimeout(() => {
      if (this.isEvaluatingExportRules) {
        this.isEvaluatingExportRules = false;
        this.exportChecklistError = 'Export rule evaluation timed out. Please try again.';
        this.cdr.detectChanges();
      }
    }, this.requestTimeoutMs + 1000);

    forkJoin({
      eventTeams: this.teamService.getEventTeamsByEventId(evt.id).pipe(timeout(this.requestTimeoutMs)),
      questionAssignments: this.questionAssignmentService.getAll().pipe(timeout(this.requestTimeoutMs))
    }).pipe(
      switchMap(({ eventTeams, questionAssignments }) => {
        const teamIds = [...new Set(eventTeams.map(assignment => assignment.teamId))];
        const questionsAssignedCount = questionAssignments.filter(assignment => assignment.eventId === evt.id).length;

        if (teamIds.length === 0) {
          return of({ teamIds, membersByTeam: [] as Array<{ teamId: number; members: unknown[] }>, questionsAssignedCount });
        }

        return forkJoin(
          teamIds.map(teamId => this.teamService.getPlayersByTeamId(teamId).pipe(
            timeout(this.requestTimeoutMs),
            map(members => ({ teamId, members }))
          ))
        ).pipe(
          map(membersByTeam => ({ teamIds, membersByTeam, questionsAssignedCount }))
        );
      }),
      finalize(() => {
        this.isEvaluatingExportRules = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: ({ teamIds, membersByTeam, questionsAssignedCount }) => {
        this.isEvaluatingExportRules = false;
        const hasTwoTeams = teamIds.length === 2;
        const eachTeamHasMultipleMembers = hasTwoTeams && membersByTeam.every(team => team.members.length > 1);
        const roundsMatchQuestionAssignments = evt.rounds === questionsAssignedCount;

        this.exportRules = [
          {
            label: 'Two teams are assigned to the event',
            details: `${teamIds.length} team(s) assigned.`,
            passed: hasTwoTeams
          },
          {
            label: 'Each team has more than one team member or user',
            details: hasTwoTeams
              ? membersByTeam.map(team => `Team ${team.teamId}: ${team.members.length} member(s)`).join(' · ')
              : 'Assign exactly two teams first.',
            passed: eachTeamHasMultipleMembers
          },
          {
            label: 'Event rounds equals assigned question count',
            details: `Rounds: ${evt.rounds} · Questions assigned: ${questionsAssignedCount}`,
            passed: roundsMatchQuestionAssignments
          }
        ];

        this.canExportCurrentEvent = this.exportRules.every(rule => rule.passed);
        this.cdr.detectChanges();
      },
      error: (error: unknown) => {
        this.isEvaluatingExportRules = false;
        this.exportChecklistError = error instanceof Error
          ? `Unable to evaluate export rules: ${error.message}`
          : 'Unable to evaluate export rules for this event.';
        this.exportRules = [];
        this.canExportCurrentEvent = false;
        this.cdr.detectChanges();
      }
    });
  }

  closeExportChecklist(): void {
    this.isExportChecklistOpen = false;
    this.isEvaluatingExportRules = false;
    this.isExportingConfiguration = false;
    this.canExportCurrentEvent = false;
    this.exportChecklistError = null;
    this.exportRules = [];
    this.exportEvent = null;
    this.exportChecklistTitle = 'Validating rules for selected event.';
    if (this.exportEvaluationWatchdogId !== null) {
      window.clearTimeout(this.exportEvaluationWatchdogId);
      this.exportEvaluationWatchdogId = null;
    }
  }

  confirmExportSettings(): void {
    if (!this.exportEvent) {
      this.exportChecklistError = 'No event is selected for export.';
      return;
    }

    if (this.isEvaluatingExportRules) {
      this.exportChecklistError = 'Please wait until export rules are fully evaluated.';
      return;
    }

    if (!this.canExportCurrentEvent) {
      this.exportChecklistError = 'Export blocked: one or more checklist rules failed. Resolve failed rules and try again.';
      return;
    }

    if (this.isExportingConfiguration) {
      return;
    }

    this.isExportingConfiguration = true;
    this.exportChecklistError = null;

    this.portableConfigExportService.exportFromServer(this.exportEvent.id).pipe(
      timeout(this.exportPackageTimeoutMs),
      finalize(() => {
        if (this.isExportChecklistOpen) {
          this.isExportingConfiguration = false;
          this.cdr.detectChanges();
        }
      })
    ).subscribe({
      next: ({ fileName, blob }) => {
        try {
          this.downloadPortableConfigBlob(fileName, blob);
          this.closeExportChecklist();
        } catch (error: unknown) {
          this.exportChecklistError = error instanceof Error
            ? `Export download failed: ${error.message}`
            : 'Export download failed.';
        }
      },
      error: (error: unknown) => {
        this.exportChecklistError = error instanceof Error
          ? (error.name === 'TimeoutError'
              ? 'Export timed out while packaging media. Please retry after reducing large media selections.'
              : error.message)
          : 'Failed to export configuration.';
      }
    });
  }

  private downloadPortableConfigBlob(fileName: string, blob: Blob): void {
    if (!blob || blob.size === 0) {
      throw new Error('The exported file was empty.');
    }

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  private todayDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  private nowTime(): string {
    const d = new Date();
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  formatTime(timeStr: string): string {
    if (!timeStr) return '—';
    const [h, m] = timeStr.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
  }

  getBookingCss(status: string): string {
    return status.toLowerCase().replace(/ /g, '-');
  }

  getOrganizationName(organizationId: number): string {
    const displayName = this.organizationNamesById.get(organizationId);
    if (displayName) {
      return displayName;
    }

    this.fetchOrganizationDisplayName(organizationId);
    return 'Loading organization…';
  }

  getAssignedTeamsLabel(evt: EventRecord): string {
    return evt.assignedTeamNames.length > 0
      ? evt.assignedTeamNames.join(', ')
      : 'None';
  }

  get filteredEvents(): EventRecord[] {
    const query = this.filterText.trim().toLowerCase();
    const filtered = !query
      ? [...this.events()]
      : this.events().filter(evt =>
          evt.name.toLowerCase().includes(query) ||
          this.getOrganizationName(this.getBookingById(evt.bookingId)?.organizationId ?? 0).toLowerCase().includes(query) ||
          evt.assignedTeamNames.some(teamName => teamName.toLowerCase().includes(query)) ||
          this.getBookingName(evt.bookingId).toLowerCase().includes(query) ||
          evt.status.toLowerCase().includes(query) ||
          this.formatDate(evt.date).toLowerCase().includes(query)
        );

    return filtered.sort((a, b) => {
      if (this.sortMode === 'name-asc') return a.name.localeCompare(b.name);
      if (this.sortMode === 'name-desc') return b.name.localeCompare(a.name);
      if (this.sortMode === 'status') return a.status.localeCompare(b.status) || a.name.localeCompare(b.name);

      const aDt = new Date(`${a.date}T${a.time || '00:00'}`).getTime();
      const bDt = new Date(`${b.date}T${b.time || '00:00'}`).getTime();
      return this.sortMode === 'oldest' ? aDt - bDt : bDt - aDt;
    });
  }
}